import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-fetching-library';
import { useSelector } from 'react-redux';
import { getJourneyAction, getDefinitionAction } from '../api';
import { JOURNEY_POLL_INTERVAL_MS } from './constants';

// Journey state with polling: engine advancement is async, so the
// drawer re-reads state on an interval while open.
//
// `definitionUuid` is the `programDefinitionUUID` the list row already
// carries. The journey detail payload does not reliably include it (per
// the backend spec), so the row's value is the primary source — the
// payload's own field is preferred only when actually present.
export const useJourneyState = (accountUuid, journeyUuid, definitionUuid) => {
  const [definition, setDefinition] = useState(null);
  const fetchedDefinitionUuidRef = useRef(null);

  const journeyAction = useMemo(
    () => getJourneyAction({ accountUuid, journeyUuid }),
    [accountUuid, journeyUuid]
  );
  const { payload: journey, loading, query } = useQuery(journeyAction, false);

  const resolvedDefinitionUuid = journey?.programDefinitionUUID || definitionUuid;

  // react-fetching-library's useQuery tolerates a null action (it is only
  // used to key the internal cache/base64 comparison); with `false` as the
  // init flag it never auto-fetches, so this stays inert until `definition`
  // is actually wanted below.
  const definitionAction = useMemo(
    () =>
      resolvedDefinitionUuid
        ? getDefinitionAction({ accountUuid, definitionUuid: resolvedDefinitionUuid })
        : null,
    [accountUuid, resolvedDefinitionUuid]
  );
  const { query: fetchDefinition } = useQuery(definitionAction, false);

  // The poll pauses while the user is idle (features/session-idle): a
  // background poller must not keep the server session alive. The next
  // interaction flips the flag back and the effect re-reads at once.
  const isUserIdle = useSelector(state => state.appSettings.isUserIdle);

  useEffect(() => {
    if (!journeyUuid || isUserIdle) return undefined;
    query();
    const timer = setInterval(query, JOURNEY_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [journeyUuid, query, isUserIdle]);

  useEffect(() => {
    if (!resolvedDefinitionUuid || fetchedDefinitionUuidRef.current === resolvedDefinitionUuid) {
      return;
    }
    fetchedDefinitionUuidRef.current = resolvedDefinitionUuid;
    fetchDefinition().then(({ payload, error }) => {
      if (!error && payload) setDefinition(payload);
    });
  }, [resolvedDefinitionUuid, fetchDefinition]);

  const refresh = useCallback(() => query(), [query]);

  return { journey, definition, loading, refresh };
};
